import { useState, useMemo, useCallback, useEffect } from 'react';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qOdontogramChartsByEncounter,
  qOdontogramChartsByPatient,
  type OdontogramChartRow,
} from '@helios/data-access';
import {
  OdontogramChart,
  OdontogramLegend,
  type ToothStatesMap,
  type ToothState,
} from './OdontogramChart';
import { ToothConditionsModal } from './ToothConditionsModal';

// ─── Helpers ───

function parseToothStates(raw: string | null): ToothStatesMap {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as ToothStatesMap;
  } catch {
    return {};
  }
}

function formatChartDate(iso: string | null): string {
  if (!iso) return 'Unknown';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Props ───

interface EncounterChartingTabProps {
  patientId: string;
  encounterId: string;
  visitId?: string | null;
}

// ─── Component ───

export function EncounterChartingTab({
  patientId,
  encounterId,
  visitId,
}: EncounterChartingTabProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';

  // ── Queries ──
  const { data: encounterCharts = [] } = usePowerSyncQuery<OdontogramChartRow>(
    qOdontogramChartsByEncounter(encounterId),
  );
  const { data: allPatientCharts = [] } = usePowerSyncQuery<OdontogramChartRow>(
    qOdontogramChartsByPatient(patientId),
  );

  // ── State ──
  const [activeChartId, setActiveChartId] = useState<string | null>(null);
  const [selectedTooth, setSelectedTooth] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showChartSelector, setShowChartSelector] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showLegend, setShowLegend] = useState(true);
  const [pendingStates, setPendingStates] = useState<ToothStatesMap | null>(null);

  // ── Derived ──
  const activeChart = useMemo(() => {
    if (activeChartId) {
      return (
        encounterCharts.find((c) => c.id === activeChartId) ??
        allPatientCharts.find((c) => c.id === activeChartId) ??
        null
      );
    }
    return encounterCharts[0] ?? null;
  }, [activeChartId, encounterCharts, allPatientCharts]);

  const isViewingOtherEncounter = activeChart != null && activeChart.encounter_id !== encounterId;

  const savedStates = useMemo(
    () => parseToothStates(activeChart?.tooth_states ?? null),
    [activeChart?.tooth_states],
  );

  // Clear pending edits when switching charts
  useEffect(() => {
    setPendingStates(null);
  }, [activeChartId]);

  // Use pending (in-memory) edits if they exist, otherwise use saved
  const toothStates = pendingStates ?? savedStates;
  const hasUnsavedChanges = pendingStates !== null;

  // Other encounter charts (not the current encounter)
  const otherCharts = useMemo(
    () => allPatientCharts.filter((c) => c.encounter_id !== encounterId),
    [allPatientCharts, encounterId],
  );

  // ── Actions ──

  const handleCreateChart = useCallback(async () => {
    if (!db || creating) return;
    setCreating(true);
    try {
      const id = crypto.randomUUID();
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO odontogram_charts
          (id, org_id, clinic_id, patient_id, encounter_id, visit_id, chart_type, status, source, tooth_states, created_by_user_id, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          id,
          orgId,
          null,
          patientId,
          encounterId,
          visitId ?? null,
          'tooth_chart',
          'draft',
          'encounter_charting',
          '{}',
          userId,
          now,
          now,
        ],
      );
      setActiveChartId(id);
    } finally {
      setCreating(false);
    }
  }, [db, orgId, patientId, encounterId, visitId, userId, creating]);

  const handleCopyChart = useCallback(async () => {
    if (!db || !activeChart || creating) return;
    setCreating(true);
    try {
      const id = crypto.randomUUID();
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO odontogram_charts
          (id, org_id, clinic_id, patient_id, encounter_id, visit_id, chart_type, status, source, tooth_states, notes, created_by_user_id, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          id,
          orgId,
          null,
          patientId,
          encounterId,
          visitId ?? null,
          'tooth_chart',
          'draft',
          'encounter_charting',
          activeChart.tooth_states ?? '{}',
          `Copied from chart ${formatChartDate(activeChart.created_at)}`,
          userId,
          now,
          now,
        ],
      );
      setActiveChartId(id);
    } finally {
      setCreating(false);
    }
  }, [db, orgId, patientId, encounterId, visitId, userId, activeChart, creating]);

  const handleToothSave = useCallback(
    (toothNum: number, state: ToothState) => {
      if (isViewingOtherEncounter) return;
      setPendingStates((prev) => ({ ...(prev ?? savedStates), [String(toothNum)]: state }));
    },
    [savedStates, isViewingOtherEncounter],
  );

  const handleToothClear = useCallback(
    (toothNum: number) => {
      if (isViewingOtherEncounter) return;
      setPendingStates((prev) => {
        const updated = { ...(prev ?? savedStates) };
        delete updated[String(toothNum)];
        return updated;
      });
    },
    [savedStates, isViewingOtherEncounter],
  );

  const handleSaveChart = useCallback(async () => {
    if (!db || !activeChart || !pendingStates || saving) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      await db.execute(
        `UPDATE odontogram_charts SET tooth_states = ?, updated_at = ? WHERE id = ?`,
        [JSON.stringify(pendingStates), now, activeChart.id],
      );
      setPendingStates(null);
    } finally {
      setSaving(false);
    }
  }, [db, activeChart, pendingStates, saving]);

  const handleDiscardChanges = useCallback(() => {
    setPendingStates(null);
  }, []);

  const handleToothClick = useCallback((num: number) => {
    setSelectedTooth(num);
    setModalOpen(true);
  }, []);

  // ── Render ──

  // Empty state
  if (encounterCharts.length === 0 && !activeChartId) {
    return (
      <div
        style={{
          padding: 'var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Charting
          </h2>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            Dental odontogram for this encounter
          </p>
        </div>

        <div
          style={{
            textAlign: 'center',
            padding: 'var(--sp-8)',
            border: '2px dashed var(--border)',
            borderRadius: 'var(--radius-lg)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--sp-3)',
          }}
        >
          <div style={{ color: 'var(--text-placeholder)' }}>
            <NavIcon name="clipboard" />
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            No tooth chart yet for this encounter.
          </div>
          <button
            onClick={handleCreateChart}
            disabled={creating}
            style={{
              height: 36,
              padding: '0 20px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: primary,
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              opacity: creating ? 0.6 : 1,
            }}
          >
            <NavIcon name="plus" />
            {creating ? 'Creating...' : 'New Part Chart'}
          </button>

          {/* Show option to copy from previous */}
          {otherCharts.length > 0 && (
            <div style={{ marginTop: 'var(--sp-2)' }}>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-placeholder)',
                }}
              >
                or copy from a previous encounter
              </span>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  marginTop: 8,
                  maxHeight: 120,
                  overflowY: 'auto',
                }}
              >
                {otherCharts.slice(0, 5).map((chart) => (
                  <button
                    key={chart.id}
                    onClick={() => {
                      setActiveChartId(chart.id);
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-surface)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    {formatChartDate(chart.created_at)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 'var(--sp-3)',
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Charting
          </h2>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            {isViewingOtherEncounter
              ? 'Viewing chart from a previous encounter (read-only)'
              : `Chart: ${formatChartDate(activeChart?.created_at ?? null)}`}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Legend toggle */}
          <button
            onClick={() => setShowLegend((v) => !v)}
            style={{
              height: 32,
              padding: '0 12px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: showLegend ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-xs)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <NavIcon name="info" />
            Legend
          </button>

          {/* Chart selector */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowChartSelector((v) => !v)}
              style={{
                height: 32,
                padding: '0 12px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text)',
                fontSize: 'var(--font-size-xs)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <NavIcon name="list" />
              Charts
              <NavIcon name="chevron-down" />
            </button>
            {showChartSelector && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 4,
                  width: 260,
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  zIndex: 50,
                  padding: 'var(--sp-2)',
                  maxHeight: 300,
                  overflowY: 'auto',
                }}
              >
                {/* Current encounter charts */}
                {encounterCharts.length > 0 && (
                  <>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text-placeholder)',
                        padding: 'var(--sp-1) var(--sp-2)',
                        fontWeight: 'var(--font-weight-semi)',
                      }}
                    >
                      This Encounter
                    </div>
                    {encounterCharts.map((chart) => (
                      <button
                        key={chart.id}
                        onClick={() => {
                          setActiveChartId(chart.id);
                          setShowChartSelector(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          padding: 'var(--sp-2) var(--sp-2)',
                          background:
                            activeChart?.id === chart.id
                              ? 'var(--bg-primary-subtle)'
                              : 'transparent',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text)',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        {formatChartDate(chart.created_at)}
                      </button>
                    ))}
                  </>
                )}

                {/* Other encounter charts */}
                {otherCharts.length > 0 && (
                  <>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text-placeholder)',
                        padding: 'var(--sp-1) var(--sp-2)',
                        fontWeight: 'var(--font-weight-semi)',
                        marginTop: encounterCharts.length > 0 ? 8 : 0,
                        borderTop: encounterCharts.length > 0 ? '1px solid var(--border)' : 'none',
                        paddingTop: encounterCharts.length > 0 ? 8 : undefined,
                      }}
                    >
                      Previous Encounters
                    </div>
                    {otherCharts.slice(0, 10).map((chart) => (
                      <button
                        key={chart.id}
                        onClick={() => {
                          setActiveChartId(chart.id);
                          setShowChartSelector(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          padding: 'var(--sp-2) var(--sp-2)',
                          background:
                            activeChart?.id === chart.id
                              ? 'var(--bg-primary-subtle)'
                              : 'transparent',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        {formatChartDate(chart.created_at)}
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Copy into this encounter */}
          {isViewingOtherEncounter && (
            <button
              onClick={handleCopyChart}
              disabled={creating}
              style={{
                height: 32,
                padding: '0 14px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: primary,
                color: '#fff',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                opacity: creating ? 0.6 : 1,
              }}
            >
              <NavIcon name="copy" />
              Copy into this encounter
            </button>
          )}

          {/* New chart */}
          {!isViewingOtherEncounter && (
            <button
              onClick={handleCreateChart}
              disabled={creating}
              style={{
                height: 32,
                padding: '0 14px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: primary,
                color: '#fff',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                opacity: creating ? 0.6 : 1,
              }}
            >
              <NavIcon name="plus" />
              New Chart
            </button>
          )}

          {/* Save Chart */}
          {hasUnsavedChanges && !isViewingOtherEncounter && (
            <>
              <button
                onClick={handleDiscardChanges}
                style={{
                  height: 32,
                  padding: '0 14px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-xs)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                Discard
              </button>
              <button
                onClick={handleSaveChart}
                disabled={saving}
                style={{
                  height: 32,
                  padding: '0 16px',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: '#16a34a',
                  color: '#fff',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-semi)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  opacity: saving ? 0.6 : 1,
                }}
              >
                <NavIcon name="check" />
                {saving ? 'Saving...' : 'Save Chart'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Read-only banner */}
      {isViewingOtherEncounter && (
        <div
          style={{
            padding: 'var(--sp-3) var(--sp-4)',
            background: '#fef3c7',
            borderRadius: 'var(--radius-md)',
            border: '1px solid #fbbf24',
            fontSize: 'var(--font-size-sm)',
            color: '#92400e',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <NavIcon name="alert-triangle" />
          This chart belongs to a previous encounter and is read-only. Use "Copy into this
          encounter" to create an editable copy.
        </div>
      )}

      {/* Legend + Chart */}
      <div style={{ display: 'flex', gap: 'var(--sp-4)', alignItems: 'stretch' }}>
        {/* Legend */}
        {showLegend && (
          <div
            style={{
              width: 180,
              flexShrink: 0,
              padding: 'var(--sp-4)',
              background: 'var(--bg-surface-2)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border)',
            }}
          >
            <OdontogramLegend />
          </div>
        )}

        {/* Chart */}
        <div
          style={{
            flex: 1,
            padding: 'var(--sp-5)',
            background: 'var(--bg-surface-2)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'center',
            overflow: 'auto',
          }}
        >
          <OdontogramChart
            toothStates={toothStates}
            selectedTooth={selectedTooth}
            onToothClick={handleToothClick}
            readOnly={isViewingOtherEncounter}
            primary={primary}
          />
        </div>
      </div>

      {/* Tooth Conditions Modal */}
      <ToothConditionsModal
        open={modalOpen}
        toothNum={selectedTooth ?? 11}
        initialState={selectedTooth ? toothStates[String(selectedTooth)] : undefined}
        onSave={handleToothSave}
        onClear={handleToothClear}
        onClose={() => {
          setModalOpen(false);
          setSelectedTooth(null);
        }}
        primary={primary}
      />
    </div>
  );
}
